import React, { Component } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';

import Session from '../utils/Session';
import { ActionType } from './ActionType.enum';
import { ItemType } from './ItemType.enum';
import { BadgeItem } from './BadgeItem.component';
import Analytics from '../utils/Analytics';
import { SegmentEventTypes } from '../data/tracking';
import { BadgeItem as BadgeItemPropTypes } from './BadgeItem.proptypes';

export class GroupItem extends Component {
	toggleHeader = event => {
		const { id, disabled, onGroupHeaderClick, dataqa } = this.props;

		if (disabled !== true) {
			Analytics.trackSegmentEvent({
				trackingData: {
					field_type: 'button',
					field_name_dataqa: `${dataqa}`,
				},
				eventType: SegmentEventTypes.UserInteraction,
			});

			onGroupHeaderClick({ groupId: id });
			event.stopPropagation();
		}
	};

	// call parent's onActionTrigger method
	raiseActionEvent = event => {
		const { id, onActionTrigger, dataqa } = this.props;
		const { action: actionType } = event.currentTarget.dataset;

		Analytics.trackSegmentEvent({
			trackingData: {
				field_type: 'button',
				field_name_dataqa: `${dataqa}-${actionType}-button`,
			},
			eventType: SegmentEventTypes.UserInteraction,
		});

		event.stopPropagation();

		onActionTrigger(actionType, ItemType.Group, { groupId: id });
	};

	childBadgeItemActionHandler = (actionType, itemType, data) => {
		const { id, onActionTrigger } = this.props;

		// call parent's onActionTrigger and pass the group id along
		onActionTrigger(actionType, itemType, { ...data, groupId: id });
	};

	renderToolButtons = () => {
		const { toolButtons, dataqa } = this.props;

		if (!isEmpty(toolButtons)) {
			return toolButtons.map((ToolButton, index) => (
				<div className="tool" key={index}>
					<ToolButton />
				</div>
			));
		}

		return (
			<>
				<div className="tool">
					<span
						className="pi pi-cog"
						data-action={ActionType.Settings}
						onClick={this.raiseActionEvent}
						tabIndex={0}
						role="button"
						aria-label="Settings"
						{...{ dataqa: `${dataqa}-settings-button` }}
					/>
				</div>

				<div className="tool">
					<span
						className="pi pi-plus"
						data-action={ActionType.Add}
						onClick={this.raiseActionEvent}
						tabIndex={0}
						role="button"
						aria-label="Add"
						{...{ dataqa: `${dataqa}-add-button` }}
					/>
				</div>
			</>
		);
	};

	renderHeader = () => {
		const { collapsed, dataqa = 'menu-item-text', showTools, title } = this.props;
		const { brandName } = Session.getBrand();
		const isEndicia = brandName === 'Endicia';

		const titlebarCls = classNames('group-item-titlebar', {
			'group-item-titlebar-with-tools': showTools === true,
		});

		const toggleIconCls = classNames('pi', {
			'pi-sort-down-fill': collapsed === true,
			'pi-sort-up-fill': collapsed !== true,
		});

		return (
			<div className={titlebarCls} onClick={this.toggleHeader} tabIndex={0} role="button">
				<div className="menu-item-text" data-qa={dataqa} {...{ dataqa }}>
					{title}
				</div>
				<div className="tools-wrapper right">
					{showTools && this.renderToolButtons()}
					<div className="tool toggle-icon visible" role="presentation">
						<i className={toggleIconCls} />
					</div>
				</div>
			</div>
		);
	};

	render() {
		const { active, collapsed, disabled = false, items = [], dataqa } = this.props;
		const className = classNames('group-item', {
			active: active === true,
			clickable: disabled !== true,
			disabled: disabled === true,
			expanded: collapsed !== true,
			collapsed: collapsed === true,
		});

		return (
			<div className={className}>
				{this.renderHeader()}
				<div className="group-item-content">
					{items.length > 0 &&
						items.map(item => (
							<BadgeItem
								{...item}
								key={item.id}
								dataqa={dataqa}
								onActionTrigger={this.childBadgeItemActionHandler}
							/>
						))}
				</div>
			</div>
		);
	}
}

GroupItem.propTypes = {
	// unique identifier
	id: PropTypes.number.isRequired,

	// item type
	type: PropTypes.oneOf([ItemType.Group]),

	// children of Group-type item
	items: PropTypes.arrayOf(PropTypes.shape(BadgeItemPropTypes)).isRequired,

	// edit and delete button
	showTools: PropTypes.bool,

	// title
	title: PropTypes.string,

	// flag indicating a child item is selected
	active: PropTypes.bool,

	// flag indicating if the group panel is collapsed
	collapsed: PropTypes.bool,

	// disabled flag
	disabled: PropTypes.bool,

	// dataqa attributes for automation purpose
	dataqa: PropTypes.string,
};
